/**
 * Desktop updates, from the web layer's side.
 *
 * Kept out of `desktop.ts` deliberately. That module's contract is that it
 * makes no network request outside the webview, and an update check is nothing
 * but one. The request itself still happens in Rust — see `updates.rs` — so the
 * contract holds; this file only crosses the IPC boundary.
 *
 * Unlike the rest of the bridge, a failure here is **not** swallowed. Every
 * other desktop affordance is optional: if `set_always_on_top` fails, the
 * window stays where it was and nothing is lost. An update that silently fails
 * to check leaves someone on an old build believing they are current, so the
 * reason travels back and gets shown.
 */
import { isDesktop } from './runtime.js';

/** Mirrors `UpdateStatus` in `apps/desktop/src-tauri/src/updates.rs`. */
export interface UpdateStatus {
  readonly available: boolean;
  readonly currentVersion: string;
  readonly version: string | null;
  readonly notes: string | null;
  readonly date: string | null;
}

export type UpdateState =
  /** On the web, or before anything has been asked. */
  | { readonly kind: 'idle' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'current'; readonly currentVersion: string }
  | { readonly kind: 'available'; readonly status: UpdateStatus }
  | { readonly kind: 'installing' }
  | { readonly kind: 'failed'; readonly reason: string };

const IDLE: UpdateState = { kind: 'idle' };

/**
 * A Tauri command error arrives as whatever the Rust side put in `Err`. Ours
 * are strings, but a panic or a plugin error can surface as something else, and
 * `[object Object]` in the UI is worse than a generic sentence.
 */
function reasonFrom(e: unknown): string {
  if (typeof e === 'string' && e.trim()) return e;
  if (e instanceof Error && e.message) return e.message;
  return 'The update check could not be completed.';
}

async function call<T>(command: string): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<T>(command);
}

export async function checkForUpdate(): Promise<UpdateState> {
  if (!isDesktop()) return IDLE;
  try {
    const status = await call<UpdateStatus>('check_for_update');
    return status.available
      ? { kind: 'available', status }
      : { kind: 'current', currentVersion: status.currentVersion };
  } catch (e) {
    return { kind: 'failed', reason: reasonFrom(e) };
  }
}

/**
 * Install and restart.
 *
 * On success this never resolves — the process is replaced. Anything it does
 * return is a failure, which is why there is no success branch to render.
 */
export async function installUpdate(): Promise<UpdateState> {
  if (!isDesktop()) return IDLE;
  try {
    await call<void>('install_update');
    // Unreachable in practice. If the restart somehow does not happen, saying
    // so beats a button that stays stuck on "Installing…" forever.
    return { kind: 'failed', reason: 'The update installed but AEGIS did not restart. Quit and reopen it.' };
  } catch (e) {
    return { kind: 'failed', reason: reasonFrom(e) };
  }
}

/** One line describing where the check got to. Empty when there is nothing to say. */
export function describe(state: UpdateState): string {
  switch (state.kind) {
    case 'checking': return 'Checking…';
    case 'current': return `AEGIS ${state.currentVersion} is the latest version.`;
    case 'available': return `Version ${state.status.version} is available.`;
    case 'installing': return 'Downloading and installing. AEGIS will restart.';
    case 'failed': return state.reason;
    case 'idle': return '';
  }
}
